import { clamp, damp, rnd } from '../core/math';
import { CAM_OFF, camera } from '../render/scene';
import { STATE, angDiff, spawnRing, tmpV } from './fx';
import { UIPREF } from '../platform/ui-prefs';
import { boss } from '../entities/enemies';
import { P } from '../world/player';
/* ============================================================
   戰場鏡頭 —— 讓它讀起來像「在駕駛」而不是「操作一個玩具」
   ============================================================

   使用者 2026-08-02：「局內的相機視角要調整，目前感受不到機甲的魅力與震撼」、
   「我要真的在駕駛機甲的感覺，而不是操作一個玩具」。

   從 `sim/player.ts` 抽出來，因為它從「三行 damp」長成了一整套規則。
   四件事，每一件都對應一個具體的感受問題：

   1. **基準角度**（`PRESETS`）：舊值 y33/z23/FOV50 的俯角 55°、距離 40，
      機體只有畫面高度的 8% —— 那個尺寸下看到的是一個棋子，不是一台機器。
      改用預設表而不是寫死常數：這是品味判斷，要能在遊戲裡直接切著看
      （除錯主控台 `cam <id>`），不是每次都重 build
   2. **密度自動拉遠**：貼近的代價是視野變短。所以周圍的敵人越多、鏡頭越退越高 ——
      空曠時是駕駛艙視角，被 200 隻圍住時自動回到看得見包圍圈的高度
   3. **速度感**：衝刺時 FOV 推開 + 跟隨變鈍（鏡頭被甩在後面），
      結束回彈。**這一項是「在駕駛」最直接的來源** —— 位移本身不會給速度感，
      鏡頭跟不上才會
   4. **重量**：走路每一步的微幅垂直震動、齊射的後座、大招壓低推近、
      Boss 降臨時鏡頭先看它一眼

   決定性：這個檔跑在**畫面幀**上（`game.ts` 的 tick），不進模擬步、
   不寫任何會被 `snapshot()` 讀到的欄位。`STATE.shake` 本來就在這裡衰減。 */

/** 基準角度的預設表。`off` 是相機相對注視點的位移，`fov` 是視角。
    比較截圖在 `.claude/notes/shots/m13-cam/`。 */
export var PRESETS = {
  'a-now': { off: [0, 33, 23], fov: 50, n: '現況（俯視棋盤）' },
  'b-mid': { off: [0, 26, 25], fov: 46, n: '稍低稍近' },
  'c-hero': { off: [0, 19, 26], fov: 42, n: '英雄視角' },
  'd-close': { off: [0, 14, 22], fov: 38, n: '貼身電影' },
  'e-pilot': { off: [0, 9.5, 17], fov: 40, n: '駕駛視角' },
  /* ---- 以下三組**鏡頭會跟著機體轉**（`yaw: true`）。

     使用者第二輪回饋：「目前視角還是不夠身歷其境、沈浸感」。
     前五組再怎麼壓低都改不掉一件事 —— **鏡頭是世界鎖定的**：
     永遠從 +z 看過去，機體轉身時轉的只有機體，畫面本身不動。
     那讀起來是「看著一個立體透視圖裡的東西」，不是「坐在它上面」。

     跟著轉之後，轉身會變成**畫面在轉**，這是沈浸感最大的單一來源。
     代價是方向感：世界鎖定時「上面永遠是北」，跟著轉就沒有這個錨點 ——
     所以 yaw 追得很慢（damp 2.2）而且以**移動方向**為主、瞄準方向為輔，
     不會因為自動瞄準跳到另一隻而甩鏡。 */
  /* ---- 「看得到場景」的一組（使用者第三輪：「幫我高一些，我要像吸血鬼那樣
     看得到場景」）。倖存者類的讀圖需求是**看得到自己被什麼包圍**，
     那需要高度；而機甲的存在感需要距離近、FOV 窄。
     `i-field` 是這兩者的折衷：比舊的 a-now 近 8 個單位、俯角低 8 度，
     但仍然是世界鎖定（高處跟著轉會讓場地在畫面裡打轉，那是真的暈）。 */
  'i-field': { off: [0, 24, 21], fov: 46, n: '戰場視野（看得到場景）' },
  'f-chase': { dist: 15, y: 7.0, fov: 46, yaw: true, n: '追尾（跟著轉）' },
  'g-shoulder': { dist: 11, y: 5.2, fov: 52, yaw: true, n: '過肩（很近）' },
  'h-cockpit': { dist: 7.5, y: 4.2, fov: 58, yaw: true, n: '駕駛艙後（極限）' }
};

export var CAM = {
  /* 預設 `b-mid`（世界鎖定、稍低稍近，off [0,26,25] / fov 46）。

     **這個欄位被改過兩次，兩次都是使用者指定的，不要憑手感再翻一次：**
       2026-08-02  b-mid  —— 密度上調那一輪
       2026-08-02  f-chase 追尾 —— 「要的是在駕駛，不是看著一個立體透視圖」
       2026-08-03  b-mid  ← 現在這個

     追尾在同屏三百隻的密度下看不到自己被包圍了（鏡頭只有 7 高、距離 15），
     而這個遊戲的走位判斷全靠「看得到場面」。三組跟轉的預設（f/g/h）都還在，
     主控台 `cam <id>` 隨時切得回去。 */
  preset: 'b-mid',
  /* 動態量，全部是「相對基準的偏移」而不是絕對值 ——
     這樣換基準角度時所有動態都自動跟著對，不用逐項重調。 */
  zoom: 0,        // 密度拉遠：0…1
  bossZoom: 0,    // Boss 體型拉遠：0…1（KAISER 48 單位高，不退就框不下）
  fovNow: 46,     // 實際 FOV（damp 追目標）
  /* 玩家自己的縮放（滑鼠滾輪）。品味判斷交給手感 ——
     我調不出「剛剛好」，但玩的人三秒就能滾到。 */
  userZoom: 1,
  kick: 0,        // 衝刺／後座的瞬間推力
  push: 0,        // 後座：沿瞄準方向往後推
  lowT: 0,        // 大招：壓低推近的剩餘時間
  focusX: 0, focusZ: 0, focusT: 0,   // Boss 進場運鏡
  yaw: 0,         // 追尾鏡頭的軌道角（只有 yaw 型預設會用）
  leadX: 0, leadZ: 0,   // 前導：鏡頭往「你正在去的方向」偏一點
  punch: 0,       // 撞擊感：瞬間的 FOV 內縮 + 回彈
  cine: 0,        // 大招演出的暫態推鏡（0…1，0.35 秒歸零）
  /* 選單推鏡（2026-08-05 UI 打磨）：升級／寶箱／暫停開著時，鏡頭平滑推近 18%。
     **不是改預設角度**（那是使用者的決定，翻過三次，memory `camera-preset-is-users-call`）
     —— 跟 `cine` 同一類的暫態乘數，選單一關就自己回去。
     跟畫面上的 `body.menu-open`（去飽和＋模糊）是同一個轉場的兩半。 */
  menu: 0,
  dust: false,    // 踏步揚塵的交替旗標（兩步一次）
  bank: 0,        // 側移時的微幅傾斜
  step: 0,        // 走路踏步的相位（重量用）
  /** 下一次 `updateCamera` 直接落到目標機位（跳過 damp），用完自動清掉。
      出擊過場（`sim/cam-intro.ts`）用它把終點釘死 —— 終點自己還在 damp 的話，
      過場插的是一個活靶，收尾會有一段對不上的滑動。 */
  snap: 0,
  dynamics: true
};

export function setCamPreset(id) {
  if (!PRESETS[id]) return null;
  CAM.preset = id;
  return PRESETS[id].n;
}

/** 衝刺／後座的推鏡。`k` 是強度（衝刺 1、齊射 0.25）。 */
export function camKick(k) { CAM.kick = Math.min(1.6, CAM.kick + k); }
/** 大招：壓低推近一段時間。 */
export function camLow(t) { CAM.lowT = Math.max(CAM.lowT, t); }
/** 撞擊感：瞬間把 FOV 往內縮再彈回來。**這是俯視鏡頭僅存的「衝擊」通道** ——
    角度不能動、距離不能動，能動的只有視角本身與震動。
    `k` 建議值：受傷 0.5、大招 0.8、Boss 踉蹌 1.0。 */
export function camPunch(k) { CAM.punch = Math.min(1.4, CAM.punch + k); }
/** 大招演出的推鏡。0…1，只留 0.35 秒。 */
export function camCine(k) { CAM.cine = Math.min(1, CAM.cine + k); }

/** Boss 進場：鏡頭先飄去看它一眼再回來。 */
export function camFocus(x, z, t) { CAM.focusX = x; CAM.focusZ = z; CAM.focusT = t; }

export function resetCamera() {
  CAM.yaw = 0; CAM.bank = 0; CAM.leadX = 0; CAM.leadZ = 0; CAM.punch = 0;
  // userZoom 刻意**不**重置：那是玩家調好的偏好，不該每一場歸零
  CAM.zoom = 0; CAM.bossZoom = 0; CAM.kick = 0; CAM.push = 0; CAM.lowT = 0; CAM.focusT = 0; CAM.step = 0;
  CAM.cine = 0; CAM.menu = 0;
  CAM.fovNow = PRESETS[CAM.preset].fov;
  camera.fov = CAM.fovNow; camera.updateProjectionMatrix();
}

export function updateCamera(dt) {
  var pre = PRESETS[CAM.preset] || PRESETS['i-field'];
  var baseY = pre.yaw ? pre.y : pre.off[1];
  var baseZ = pre.yaw ? pre.dist : pre.off[2];
  var baseFov = pre.fov;
  baseY *= CAM.userZoom; baseZ *= CAM.userZoom;
  /* ---- 大招演出的推鏡（`sim/ult-stage.ts`）。**這不是改鏡頭預設** ——
     預設角度是使用者自己調的、翻過三次，不准憑手感動（memory:
     `camera-preset-is-users-call`）。`cine` 是一個 0.35 秒就歸零的暫態乘數，
     只在大招發射的那一拍把距離縮 12%，之後自己彈回，不留任何殘留。 */
  CAM.cine = Math.max(0, CAM.cine - dt * 3.4);
  if (CAM.cine > 0) {
    var ci = 1 - 0.12 * CAM.cine;
    baseY *= ci; baseZ *= ci; baseFov -= CAM.cine * 2.5;
  }
  /* ---- 選單推鏡。**這一段在模擬暫停時仍然跑**：`game.ts` 的 else 分支
     每幀都會呼叫 updateCamera，所以選單開著的那 0.35 秒鏡頭是活的。
     推近 18%＋FOV 收 3.5 度，跟 CSS 的去飽和／模糊配成同一個轉場。 */
  CAM.menu = damp(CAM.menu, (STATE.reward || STATE.paused) ? 1 : 0, 6.5, dt);
  if (CAM.menu > 0.002) {
    var mk = 1 - 0.18 * CAM.menu;
    baseY *= mk; baseZ *= mk; baseFov -= CAM.menu * 3.5;
  }

  if (CAM.dynamics) {
    /* ---- 密度自動拉遠。`STATE.near20` 由 `sim/enemies.ts` 每步順手數的
       （那個迴圈本來就在算距離，多一個計數幾乎免費）。
       用**平滑**的目標值而不是直接跳：密度是脈衝的，跟著跳會讓鏡頭抽搐。 */
    var want = clamp((STATE.near20 - 6) / 34, 0, 1);
    CAM.zoom = damp(CAM.zoom, want, 1.2, dt);
    baseY += CAM.zoom * 14; baseZ += CAM.zoom * 9; baseFov += CAM.zoom * 6;

    /* ---- Boss 體型自動拉遠（B2）。**這不是改預設角度** ——
       預設（`CAM.preset`）是使用者的決定，被翻過三次，這裡一行都不碰它；
       這是跟「密度自動拉遠」同一類的**動態偏移**，只在 Boss 在場時生效。

       理由是體型差 2.3 倍：WALZE 世界高度 6.6，KAISER 是 14.9
       （已經被 `bossFieldScale` 軟性壓縮過，原本是 48）。
       b-mid 在 Boss 那個距離上的可視高度只有 22 單位，退完約 27 ——
       12 以上的一律吃滿拉遠（實測 `tools/boss-frame-probe.js` 的入鏡率）。 */
    /* 拉遠量**跟著 Boss 的世界高度線性長**，不是一個固定的偏移 ——
       這樣改體型（`bossFieldScale`）時鏡頭自動跟著對，不用兩邊各調一次。
       係數 0.9/0.62 是從「b-mid 在 Boss 距離上的可視高度約 22」反推的：
       每多 1 單位的 Boss 高度，要多 0.9 的鏡頭高度才框得回來。 */
    var bh = boss && boss.alive ? (boss.T.worldH || 0) : 0;
    CAM.bossZoom = damp(CAM.bossZoom, bh, 1.0, dt);
    baseY += CAM.bossZoom * 0.9; baseZ += CAM.bossZoom * 0.62; baseFov += clamp(CAM.bossZoom * 0.28, 0, 5);

    // ---- 速度感：高速時 FOV 微增；衝刺的 kick 疊在上面
    var sp = Math.hypot(P.vel.x, P.vel.z);
    var spK = clamp(sp / Math.max(0.01, P.s.speed), 0, 1.4);
    baseFov += spK * 3 + CAM.kick * 7;
    CAM.kick = Math.max(0, CAM.kick - dt * 3.2);
    /* 撞擊：FOV 先內縮再彈回。內縮而不是外擴 —— 外擴讀起來像「加速」，
       內縮才讀成「被撞了一下」。 */
    baseFov -= CAM.punch * 5;
    CAM.punch = Math.max(0, CAM.punch - dt * 4.2);

    // ---- 大招：壓低推近。招式本身有 2.3–4 秒，鏡頭只吃前段
    if (CAM.lowT > 0) {
      CAM.lowT -= dt;
      var lk = Math.min(1, CAM.lowT * 2);
      baseY -= lk * 4.5; baseZ -= lk * 3.0; baseFov -= lk * 3;
    }
    /* ---- 走路的重量。步頻由實際速度反解（與 `mech-anim.ts` 的走路段同源），
       每一步在**落地那一拍**給一個極小的垂直震動。
       幅度刻意小到「說不出來哪裡不一樣」—— 大了會暈，而暈不是重量。 */
    if (sp > 0.6 && P.alive) {
      CAM.step += dt * (2.2 + sp * 0.55);
      if (CAM.step >= Math.PI) {
        CAM.step -= Math.PI;
        STATE.shake = Math.min(1.4, STATE.shake + 0.045);
        /* 踏步揚塵：腳邊一圈很淡的環。**重量的視覺證據** ——
           震動說「有東西落地」，揚塵說「落地的是很重的東西」。
           環池只有 30 個，所以做得短命（0.26 秒）且兩步才一次。 */
        CAM.dust = !CAM.dust;
        if (CAM.dust) spawnRing(P.viewX, P.viewZ, 0x9fb4cc, 0.6, 3.4, 0.26);
      }
    }
  }

  /* 注視點：玩家前方 3.5 單位（照瞄準方向），這是既有的「前置量」。
     Boss 進場運鏡把注視點暫時拉向 Boss —— **只動注視點不動相機軌道**，
     否則機體會繞著偏移的圓心跑（機庫那一輪記過這個坑）。 */
  /* **前導**：注視點往「正在去的方向」再推一段（世界鎖定的鏡頭也做得到）。
     俯視鏡頭最沒有沈浸感的地方是「你永遠在畫面正中央」—— 那讀起來像棋子。
     讓畫面在移動時往前讓半格，機體就變成「跑在畫面裡」而不是「被畫面拖著」。
     用 damp 追而不是直接加：直接加會讓畫面隨著轉向抽動。 */
  var sp0 = Math.hypot(P.vel.x, P.vel.z), mx0 = 0, mz0 = 0;
  if (sp0 > 0.2) {
    var k0 = Math.min(1, sp0 / Math.max(0.01, P.s.speed)) * 6.5;
    mx0 = P.vel.x / sp0 * k0; mz0 = P.vel.z / sp0 * k0;
  }
  CAM.leadX = damp(CAM.leadX, mx0, 2.2, dt);
  CAM.leadZ = damp(CAM.leadZ, mz0, 2.2, dt);
  var lx = P.viewX + Math.sin(P.faceAim) * 3.5 + CAM.leadX;
  var lz = P.viewZ + Math.cos(P.faceAim) * 3.5 + CAM.leadZ;
  if (CAM.focusT > 0) {
    CAM.focusT -= dt;
    var f = Math.sin(clamp(CAM.focusT, 0, 1.5) / 1.5 * Math.PI) * 0.55;   // 去了再回來
    lx += (CAM.focusX - lx) * f; lz += (CAM.focusZ - lz) * f;
  }
  tmpV.set(lx, 0, lz);

  // 後座：沿瞄準的反方向推一點點
  CAM.push = Math.max(0, CAM.push - dt * 4.0);
  var bx = -Math.sin(P.faceAim) * CAM.push * 1.6, bz = -Math.cos(P.faceAim) * CAM.push * 1.6;

  /* 跟隨的軟硬：衝刺時故意變鈍（鏡頭被甩在後面 → 速度感）。
     `damp` 的係數就是「鏡頭有多想追上你」。 */
  var k = P.dashT > 0 ? 3.2 : 6;
  var tx = tmpV.x + CAM_OFF.x + bx, tz = tmpV.z + baseZ + bz;

  if (pre.yaw) {
    /* ---- 跟著機體轉的軌道。目標角以**移動方向**為主：
       自動瞄準會跳到另一隻目標，跟著瞄準轉會讓畫面被甩來甩去；
       而玩家真正在控制的是走位（支柱四：火力自動、走位手動）。
       站著不動時才回去看瞄準方向 —— 那時候畫面本來就該朝著開火的方向。 */
    var sp2 = Math.hypot(P.vel.x, P.vel.z);
    var want2 = sp2 > Math.max(0.01, P.s.speed) * 0.28 ? P.faceMove : P.faceAim;
    CAM.yaw += angDiff(want2, CAM.yaw) * Math.min(1, dt * (P.dashT > 0 ? 1.4 : 2.2));
    var sy = Math.sin(CAM.yaw), cy = Math.cos(CAM.yaw);
    // 注視點推到機體前方（跟著轉之後，「前方」才是玩家要看的地方）
    var lead = 4.2 + CAM.zoom * 3;
    tmpV.set(P.viewX + sy * lead, 0, P.viewZ + cy * lead);
    tx = tmpV.x - sy * (baseZ + CAM.push * 2.2);
    tz = tmpV.z - cy * (baseZ + CAM.push * 2.2);
  }

  /* **推進器高度：鏡頭只跟 60%。**（使用者拍板）跟滿的話任何高度都是同一個
     取景，飛起來完全感覺不到自己在高處；完全不跟則 12 單位會頂出畫面。
     跟 60% 的意思是：你在畫面上往上跑掉 40% 的高度 —— 看得出飛多高，
     又不會看不到自己的降落點。⚠️ `CAM.preset` 一個字都沒動，
     這跟密度拉遠、`bossZoom` 一樣是疊在預設之上的動態偏移。 */
  var flyY = P.viewY * 0.6;
  var fovT = clamp(baseFov, 30, 62);
  if (CAM.snap) {
    // 開局／過場的第一幀：直接到位，不從上一個畫面的位置滑過來
    camera.position.set(tx, baseY + flyY, tz);
    CAM.fovNow = fovT; CAM.snap = 0;
  } else {
    camera.position.x = damp(camera.position.x, tx, k, dt);
    camera.position.y = damp(camera.position.y, baseY + flyY, k, dt);
    camera.position.z = damp(camera.position.z, tz, k, dt);
    CAM.fovNow = damp(CAM.fovNow, fovT, 5, dt);
  }
  if (Math.abs(camera.fov - CAM.fovNow) > 0.01) { camera.fov = CAM.fovNow; camera.updateProjectionMatrix(); }

  STATE.shake = Math.max(0, STATE.shake - dt * 3.4);
  /* 使用者的震動強度只乘在**套用的這一刻**，不改 `STATE.shake` 本身 ——
     那個值會被別的邏輯讀（衰減、快照），乘在來源上等於改了模擬。
     0 = 完全關掉（暈動症的無障礙選項），設定頁的「畫面震動」滑桿。 */
  var s = STATE.shake * STATE.shake * UIPREF.shake;
  camera.position.x += rnd(-1, 1) * s * 1.6;   // det-ok: 純視覺：鏡頭震動
  camera.position.y += rnd(-1, 1) * s * 1.0;   // det-ok: 純視覺：鏡頭震動
  camera.position.z += rnd(-1, 1) * s * 1.6;   // det-ok: 純視覺：鏡頭震動
  /* 注視高度跟著鏡頭高度走：貼身視角要抬頭看機體（1.6 是舊的俯視值，
     在 y5 的過肩視角下會把機體切掉一半）。 */
  camera.lookAt(tmpV.x, 1.6 + Math.max(0, (16 - baseY)) * 0.16 + flyY, tmpV.z);
  /* **側移時的微幅傾斜。** 這是「有人在開它」最便宜的一個訊號：
     真的載具轉向時駕駛艙會傾，而傾斜量必須小 —— 大了會變成暈船。
     只在跟著轉的預設上做：世界鎖定的鏡頭傾斜會讓地平線歪掉、讀起來像 bug。 */
  if (pre.yaw) {
    var rx = Math.cos(CAM.yaw), rz = -Math.sin(CAM.yaw);      // 鏡頭的右方向
    var lat = (P.vel.x * rx + P.vel.z * rz) / Math.max(0.01, P.s.speed);
    CAM.bank = damp(CAM.bank, clamp(lat, -1, 1) * 0.055, 3, dt);
    camera.rotateZ(CAM.bank);
  } else CAM.bank = 0;
}

/** 滑鼠滾輪：整體縮放。上限刻意寬（0.55–1.9）—— 這是給人調手感的旋鈕，
    不是平衡參數，卡太死等於沒有。 */
export function camWheel(dy) {
  CAM.userZoom = clamp(CAM.userZoom * (dy > 0 ? 1.08 : 1 / 1.08), 0.55, 1.9);
  return CAM.userZoom;
}

/** 目前的實際值（給主控台印出來 —— 玩家滾到滿意之後要報得出數字）。 */
export function camReadout() {
  var pre = PRESETS[CAM.preset];
  var y = (pre.yaw ? pre.y : pre.off[1]) * CAM.userZoom;
  var d = (pre.yaw ? pre.dist : pre.off[2]) * CAM.userZoom;
  return CAM.preset + '（' + pre.n + '）　高度 ' + y.toFixed(1) + '　距離 ' + d.toFixed(1)
    + '　FOV ' + pre.fov + '　縮放 ' + CAM.userZoom.toFixed(2);
}

/** 給除錯主控台列出可用預設。 */
export function camPresetList() {
  var out = [];
  for (var k in PRESETS) out.push(k + '（' + PRESETS[k].n + '）');
  return out.join('  ');
}